import { useAppDispatch, useAppSelector } from 'app/hooks';
import TonnageConflictDialog from 'components/common/tonnage-conflict-dialog';
import { setSystemTonnageRecord } from 'features/structure/structure-slice';
import {
  rearmDuctedTonnageSeed,
  setDuctedCombinationFilters
} from 'features/ui';
import {
  TONNAGE_FILTER_FIELD,
  tonnageRangeOf,
  tonnageSeedKeyOf
} from 'features/ui/tonnage-filter-seed';
import useStructureTonnage from 'hooks/use-structure-tonnage';
import { useCallback, useState } from 'react';
import { selectIsDetailedSizingPreference } from 'utils/project';
import {
  equipmentVersusCalc,
  resolveFilterEffectiveTonnage,
  resolveTonnageFilterRange,
  shouldOpenTonnageConflict,
  TonnageRecord,
  tonnageFilterSource,
  tonnageRangeLabel,
  tonnageSourcePhrase,
  tonnageSpan
} from 'utils/tonnage-logic';
import { persistTonnageRecord } from 'utils/tonnage-persist';

// The active-size banner under the Choose Systems filters (mock S5/S6):
// source-labeled chip + one-click escape, the size-filter-off state's
// re-apply (a persisted dismissal or a manual facet clear), and the passive
// "review" affix that opens the conflict dialog for users who reached CS by
// tab click without passing the L&S Next. Renders nothing when no size
// story exists. The seed itself is applied store-wide by
// TonnageFilterSeedWatcher (hooks/use-tonnage-filter-seed).
export default function TonnageFilterBanner() {
  const dispatch = useAppDispatch();
  const { system, entry, record, equipment, effective, calc, loaded } =
    useStructureTonnage();
  const coolingBtu = useAppSelector((state) => state.project.totalBTUs.cooling);
  const detailedEstimate = useAppSelector(selectIsDetailedSizingPreference);
  const filters = useAppSelector((state) => state.ui.ductedFilters.filters);
  const appliedSeedKey = useAppSelector(
    (state) => state.ui.ductedFilters.tonnageSeedKey
  );
  const conflictOpenState = useState(false);
  const [, setConflictOpen] = conflictOpenState;

  const activeTonnageFilter = tonnageRangeOf(filters);

  const writeRecord = useCallback(
    (tonnage: TonnageRecord) => {
      if (!system) return;
      dispatch(
        setSystemTonnageRecord({ systemUniqueId: system.uniqueId, tonnage })
      );
      persistTonnageRecord(system.uniqueId, entry, tonnage);
    },
    [dispatch, system, entry]
  );

  const handleShowAllSizes = useCallback(() => {
    // filterDismissed persists so a reload never re-ambushes the opt-out
    // (§C); the effective tonnage itself stays saved and visible on EE.
    const base: TonnageRecord = record ?? {
      tons: effective?.tons ?? [],
      source: effective?.source ?? 'manual'
    };
    writeRecord({ ...base, filterDismissed: true });
    dispatch(
      setDuctedCombinationFilters(
        filters.filter((filter) => filter.field !== TONNAGE_FILTER_FIELD)
      )
    );
  }, [record, effective, writeRecord, dispatch, filters]);

  const handleReapply = useCallback(() => {
    if (record?.filterDismissed) {
      writeRecord({ ...record, filterDismissed: false });
      return;
    }
    dispatch(rearmDuctedTonnageSeed());
  }, [record, writeRecord, dispatch]);

  const handleConflictResolve = useCallback(
    (resolved: TonnageRecord) => {
      writeRecord(resolved);
      setConflictOpen(false);
    },
    [writeRecord, setConflictOpen]
  );

  if (!system || !loaded) return null;

  const filterRange = resolveTonnageFilterRange({
    record,
    components: system.components,
    coolingBtu
  });
  const filterSource = tonnageFilterSource(record, equipment, effective, calc);
  const filterEffective = resolveFilterEffectiveTonnage({
    record,
    equipment,
    calc
  });

  // Passive review affix (§C mismatch catch-all): same predicate as the L&S
  // Next trigger, but against the size the chip/filter is showing — the
  // live old-unit size on 'use-equipment', not the form tiles.
  const needsReview =
    !!activeTonnageFilter &&
    shouldOpenTonnageConflict({
      effective: filterEffective,
      calc,
      record
    });

  const dialog =
    filterEffective && calc ? (
      <TonnageConflictDialog
        openState={conflictOpenState}
        effective={filterEffective}
        calc={calc}
        record={record}
        onResolve={handleConflictResolve}
      />
    ) : null;

  if (activeTonnageFilter) {
    const label = tonnageRangeLabel(
      tonnageSpan(activeTonnageFilter.min, activeTonnageFilter.max)
    );
    // The calc is the size story and the old unit sits outside it: say why
    // the chip isn't the size that's installed.
    const oldUnit =
      filterSource === 'calc' ? equipmentVersusCalc(equipment, calc) : null;
    return (
      <div
        data-testid='tonnage-filter-banner'
        className='flex flex-col items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2'
      >
        <span className='rounded-full border border-blue-200 bg-white px-2.5 py-0.5 text-xs font-semibold text-blue-800'>
          Sized for you: {label}
        </span>
        {filterSource && (
          <span className='text-xs text-gray-600'>
            {tonnageSourcePhrase(filterSource, { detailedEstimate })}
            {oldUnit && equipment && (
              <>
                {' '}
                · your existing {equipment.raw}-ton {equipment.unitLabel} is{' '}
                {oldUnit} than your load needs
              </>
            )}
          </span>
        )}
        {needsReview && calc && (
          <span className='text-xs text-gray-600'>
            · new calculation suggests {tonnageRangeLabel(calc.tons)}{' '}
            <button
              type='button'
              className='font-medium text-blue-600 underline hover:text-blue-700'
              onClick={() => setConflictOpen(true)}
            >
              review
            </button>
          </span>
        )}
        <button
          type='button'
          className='text-xs font-medium text-blue-600 underline hover:text-blue-700'
          onClick={handleShowAllSizes}
        >
          Show all sizes
        </button>
        {dialog}
      </div>
    );
  }

  const seedOffered =
    !!filterRange && appliedSeedKey === tonnageSeedKeyOf(filterRange);

  if (filterRange && (record?.filterDismissed || seedOffered)) {
    return (
      <div
        data-testid='tonnage-filter-banner'
        className='flex flex-col items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'
      >
        <span className='rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-gray-600'>
          Size filter off
        </span>
        <span className='text-xs text-gray-600'>Showing all sizes</span>
        <button
          type='button'
          className='text-xs font-medium text-blue-600 underline hover:text-blue-700'
          onClick={handleReapply}
        >
          Re-apply {tonnageRangeLabel([filterRange.min, filterRange.max])}
        </button>
      </div>
    );
  }

  return null;
}
